import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";

/**
 * Receivables — full unpaid-invoice ledger.
 * Pulls the existing /api/invoices/client list, filters status != 'paid',
 * and surfaces the receivables-aging metric inline.
 * Future: bulk reminder via Resend, per-client rollup.
 */
export default function Receivables() {
  const [items, setItems] = useState([]);
  const [aging, setAging] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => {
    setBusy(true);
    try {
      const [inv, ag] = await Promise.all([
        api.get("/invoices/client"),
        api.get("/analytics/receivables-aging").catch(() => ({ data: null })),
      ]);
      const unpaid = (Array.isArray(inv.data) ? inv.data : inv.data.items || []).filter(i => i.status !== "paid");
      unpaid.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setItems(unpaid);
      setAging(ag.data);
    } finally { setBusy(false); }
  })(); }, []);

  const dayAge = (iso) => {
    if (!iso) return "—";
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) + "d";
  };
  const tone = (iso) => {
    if (!iso) return "badge";
    const age = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (age <= 30) return "badge badge-success";
    if (age <= 60) return "badge badge-warn";
    if (age <= 90) return "badge badge-warn";
    return "badge badge-danger";
  };

  return (
    <div className="space-y-5" data-testid="receivables-page">
      <div>
        <div className="label-eyebrow">Finance</div>
        <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Receivables</h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1">Outstanding client invoices · live ledger.</div>
      </div>

      {aging && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="receivables-buckets">
          {["0-30", "31-60", "61-90", "90+"].map(k => {
            const b = aging.buckets[k] || { count: 0, amount_inr: 0 };
            return (
              <div key={k} className="card-flat p-4" data-testid={`recv-bucket-${k}`}>
                <div className="label-eyebrow">{k} days</div>
                <div className="font-display font-bold tnum text-[22px] mt-1">{fmtMoney(b.amount_inr, "INR")}</div>
                <div className="text-[10px] text-[var(--span-text-2)] mt-0.5 font-mono">{b.count} invoices</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card-flat">
        <div className="px-5 py-3 border-b border-[var(--span-border)] flex items-center justify-between">
          <div className="label-eyebrow">Unpaid · {items.length} invoices</div>
          <div className="font-mono text-[11px] text-[var(--span-text-2)]">
            Total outstanding: {fmtMoney(aging?.total_outstanding_inr || 0, "INR")}
          </div>
        </div>
        <table className="span-table">
          <thead><tr><th>Invoice #</th><th>Client</th><th>Date</th><th>Age</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
          <tbody>
            {busy && <tr><td colSpan={6} className="text-center py-6 text-[11px] font-mono text-[var(--span-text-2)]">LOADING…</td></tr>}
            {!busy && items.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-[12px] text-[var(--span-text-2)]">All caught up — no outstanding invoices.</td></tr>}
            {items.slice(0, 200).map(i => (
              <tr key={i.invoice_id}>
                <td className="font-mono text-[12px] font-semibold"><Link to={`/invoices`} className="hover:underline">{i.invoice_number}</Link></td>
                <td className="text-[var(--span-text-2)] truncate max-w-[260px]">{i.client_id?.slice(0, 12) || "—"}</td>
                <td className="font-mono text-[11px] text-[var(--span-text-2)]">{fmtDate(i.created_at)}</td>
                <td><span className={tone(i.created_at)}>{dayAge(i.created_at)}</span></td>
                <td className="text-right tnum font-mono">{fmtMoney(i.amount_to_be_received || i.total || 0, i.currency || "INR")}</td>
                <td><span className="badge badge-info">{i.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
